// ╔══════════════════════════════════════════════════════════════════╗
// ║  HEADY™ Governance Dashboard v1.0.0                           ║
// ║  Structural integrity control center — 6-panel governance     ║
// ║  © 2026 HeadySystems Inc. — Eric Haywood, Founder             ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PHI = (1 + Math.sqrt(5)) / 2;

// ── Governance Data Store (live state) ──────────────────────────────────
const governanceState = {
  ctdVersion: '1.0.0',
  instructionStackVersion: '9.0.0',
  skillRegistryCount: 67,
  activeToolsMatrixStatus: 'COMPLIANT',
  lastAuditStatus: 'PASSED',
  openViolations: 0,
  driftFlags: 1,
  pendingChangeRequests: 3,

  instructionIntegrity: {
    duplicateRules: 2,
    conflictingDirectives: 0,
    absoluteRuleCount: 14,
    crossLayerContradictions: 0,
    compressionRatio: 0.87,
    ruleCount: 142,
    trend: 'stable',
  },

  toolCompliance: {
    skillCreatorCompliance: 100,
    imageGenCompliance: 100,
    webUsageCorrectness: 98.5,
    apiToolSchemaAdherence: 100,
    writingBlockCompliance: 97.2,
    totalViolations: 3,
    lastTestRun: 'Passed — 5/5 prompts',
  },

  skillRegistry: [
    { name: 'heady-deep-scan', owner: 'CoreTeam', version: '2.1.0', triggerValidated: true, toolDepsVerified: true, driftFlag: false, status: 'active' },
    { name: 'heady-memory-ops', owner: 'CoreTeam', version: '3.0.0', triggerValidated: true, toolDepsVerified: true, driftFlag: false, status: 'active' },
    { name: 'heady-multi-model', owner: 'CoreTeam', version: '2.5.0', triggerValidated: true, toolDepsVerified: true, driftFlag: false, status: 'active' },
    { name: 'heady-deployment', owner: 'OpsTeam', version: '1.8.0', triggerValidated: true, toolDepsVerified: true, driftFlag: false, status: 'active' },
    { name: 'heady-security-audit', owner: 'SecTeam', version: '1.3.0', triggerValidated: true, toolDepsVerified: true, driftFlag: false, status: 'active' },
    { name: 'heady-bee-swarm-ops', owner: 'CoreTeam', version: '4.0.0', triggerValidated: true, toolDepsVerified: true, driftFlag: true, status: 'active' },
    { name: 'heady-csl-engine', owner: 'CoreTeam', version: '3.2.0', triggerValidated: true, toolDepsVerified: true, driftFlag: false, status: 'active' },
    { name: 'heady-edge-ai', owner: 'EdgeTeam', version: '1.1.0', triggerValidated: true, toolDepsVerified: false, driftFlag: false, status: 'active' },
    { name: 'heady-sacred-geometry', owner: 'DesignTeam', version: '2.0.0', triggerValidated: true, toolDepsVerified: true, driftFlag: false, status: 'active' },
    { name: 'heady-trading-compliance', owner: 'FinTeam', version: '1.5.0', triggerValidated: true, toolDepsVerified: true, driftFlag: false, status: 'active' },
  ],

  changeControl: [
    { id: 'CHG-001', layer: 'Skill Registry', cascadeCompleted: true, documentationUpdated: true, logged: true, status: 'closed' },
    { id: 'CHG-002', layer: 'Tool Matrix', cascadeCompleted: true, documentationUpdated: true, logged: true, status: 'closed' },
    { id: 'CHG-003', layer: 'Instruction Stack', cascadeCompleted: false, documentationUpdated: false, logged: true, status: 'pending' },
    { id: 'CHG-004', layer: 'CTD', cascadeCompleted: false, documentationUpdated: false, logged: true, status: 'pending' },
    { id: 'CHG-005', layer: 'Governance Dashboard', cascadeCompleted: false, documentationUpdated: false, logged: true, status: 'pending' },
  ],

  entropy: {
    skillCountGrowth: '+3 (last 30d)',
    instructionLengthGrowth: '+2.1%',
    toolRuleComplexity: 'stable',
    exceptionClauses: 4,
    documentationMismatches: 1,
    trend: 'low',
  },
};

// ── Status Badge ────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const colors = {
    COMPLIANT: '#10b981',
    PASSED: '#10b981',
    active: '#10b981',
    closed: '#6b7280',
    pending: '#f59e0b',
    DEGRADED: '#f59e0b',
    FAILED: '#ef4444',
    deprecated: '#ef4444',
    stable: '#10b981',
    rising: '#f59e0b',
    critical: '#ef4444',
    low: '#10b981',
  };

  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      background: `${colors[status] || '#6b7280'}22`,
      color: colors[status] || '#6b7280',
      border: `1px solid ${colors[status] || '#6b7280'}44`,
    }}>
      {status}
    </span>
  );
};

// ── Metric Card ─────────────────────────────────────────────────────────
const MetricCard = ({ label, value, status, icon }) => (
  <div className="gov-metric-card">
    <div className="gov-metric-icon">{icon}</div>
    <div className="gov-metric-body">
      <span className="gov-metric-label">{label}</span>
      <span className="gov-metric-value">{value}</span>
    </div>
    {status && <StatusBadge status={status} />}
  </div>
);

// ── Progress Bar ────────────────────────────────────────────────────────
const ProgressBar = ({ value, max = 100, color = '#0ea5e9' }) => (
  <div style={{ width: '100%', height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${(value / max) * 100}%` }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      style={{ height: '100%', background: color, borderRadius: '3px' }}
    />
  </div>
);

// ── Panel 1: Structural Health Overview ─────────────────────────────────
const StructuralHealthPanel = () => (
  <div className="gov-panel">
    <h3 className="gov-panel-title">
      <span className="gov-panel-icon">🏗️</span>
      Structural Health Overview
    </h3>
    <div className="gov-metrics-grid">
      <MetricCard label="CTD Version" value={governanceState.ctdVersion} icon="📜" />
      <MetricCard label="Instruction Stack" value={`v${governanceState.instructionStackVersion}`} icon="📋" />
      <MetricCard label="Skills Registered" value={governanceState.skillRegistryCount} icon="⚡" />
      <MetricCard label="Tool Matrix" value={governanceState.activeToolsMatrixStatus} status={governanceState.activeToolsMatrixStatus} icon="🔧" />
      <MetricCard label="Last Audit" value={governanceState.lastAuditStatus} status={governanceState.lastAuditStatus} icon="✅" />
      <MetricCard label="Open Violations" value={governanceState.openViolations} icon="🚨" />
      <MetricCard label="Drift Flags" value={governanceState.driftFlags} status={governanceState.driftFlags > 0 ? 'pending' : 'COMPLIANT'} icon="📡" />
      <MetricCard label="Pending Changes" value={governanceState.pendingChangeRequests} status={governanceState.pendingChangeRequests > 0 ? 'pending' : 'COMPLIANT'} icon="📝" />
    </div>
  </div>
);

// ── Panel 2: Instruction Integrity ──────────────────────────────────────
const InstructionIntegrityPanel = () => {
  const ii = governanceState.instructionIntegrity;
  return (
    <div className="gov-panel">
      <h3 className="gov-panel-title">
        <span className="gov-panel-icon">📏</span>
        Instruction Integrity
      </h3>
      <div className="gov-integrity-grid">
        <div className="gov-integrity-item">
          <span className="gov-integrity-label">Duplicate Rules</span>
          <span className={`gov-integrity-value ${ii.duplicateRules > 3 ? 'warn' : ''}`}>{ii.duplicateRules}</span>
        </div>
        <div className="gov-integrity-item">
          <span className="gov-integrity-label">Conflicting Directives</span>
          <span className={`gov-integrity-value ${ii.conflictingDirectives > 0 ? 'error' : 'good'}`}>{ii.conflictingDirectives}</span>
        </div>
        <div className="gov-integrity-item">
          <span className="gov-integrity-label">Absolute Rules (ALWAYS/NEVER)</span>
          <span className="gov-integrity-value">{ii.absoluteRuleCount}</span>
        </div>
        <div className="gov-integrity-item">
          <span className="gov-integrity-label">Cross-Layer Contradictions</span>
          <span className={`gov-integrity-value ${ii.crossLayerContradictions > 0 ? 'error' : 'good'}`}>{ii.crossLayerContradictions}</span>
        </div>
        <div className="gov-integrity-item">
          <span className="gov-integrity-label">Compression Ratio</span>
          <span className="gov-integrity-value">{(ii.compressionRatio * 100).toFixed(0)}%</span>
          <ProgressBar value={ii.compressionRatio * 100} color="#8b5cf6" />
        </div>
        <div className="gov-integrity-item">
          <span className="gov-integrity-label">Total Rule Count</span>
          <span className="gov-integrity-value">{ii.ruleCount}</span>
        </div>
      </div>
      <div className="gov-trend-indicator">
        Trend: <StatusBadge status={ii.trend} />
      </div>
    </div>
  );
};

// ── Panel 3: Tool Compliance ────────────────────────────────────────────
const ToolCompliancePanel = () => {
  const tc = governanceState.toolCompliance;
  const complianceItems = [
    { tool: 'skill-creator', compliance: tc.skillCreatorCompliance, mandatory: 'Skill creation/explanation', forbidden: 'User explicitly forbids' },
    { tool: 'image_gen', compliance: tc.imageGenCompliance, mandatory: 'Image generation', forbidden: 'Non-image tasks' },
    { tool: 'web', compliance: tc.webUsageCorrectness, mandatory: 'Time-sensitive/current info', forbidden: 'General reasoning' },
    { tool: 'api_tool', compliance: tc.apiToolSchemaAdherence, mandatory: 'Skill inspection/browsing', forbidden: 'General browsing' },
    { tool: 'writing_block', compliance: tc.writingBlockCompliance, mandatory: 'Email/chat/social', forbidden: 'Content references' },
  ];

  return (
    <div className="gov-panel">
      <h3 className="gov-panel-title">
        <span className="gov-panel-icon">🔧</span>
        Tool Compliance Matrix
      </h3>
      <div className="gov-table-wrap">
        <table className="gov-table">
          <thead>
            <tr>
              <th>Tool</th>
              <th>Compliance</th>
              <th>Mandatory When</th>
              <th>Forbidden When</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {complianceItems.map((item, i) => (
              <tr key={i}>
                <td><code>{item.tool}</code></td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ProgressBar value={item.compliance} color={item.compliance >= 98 ? '#10b981' : item.compliance >= 90 ? '#f59e0b' : '#ef4444'} />
                    <span style={{ fontSize: '12px', fontWeight: 600, minWidth: '40px' }}>{item.compliance}%</span>
                  </div>
                </td>
                <td style={{ fontSize: '11px', color: '#94a3b8' }}>{item.mandatory}</td>
                <td style={{ fontSize: '11px', color: '#94a3b8' }}>{item.forbidden}</td>
                <td><StatusBadge status={item.compliance >= 98 ? 'COMPLIANT' : 'DEGRADED'} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="gov-compliance-summary">
        <span>Total Violations: <strong>{tc.totalViolations}</strong></span>
        <span>Last Test Suite: <strong>{tc.lastTestRun}</strong></span>
      </div>
    </div>
  );
};

// ── Panel 4: Skill Registry ─────────────────────────────────────────────
const SkillRegistryPanel = () => {
  const [filter, setFilter] = useState('');
  const skills = governanceState.skillRegistry.filter(s =>
    s.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="gov-panel">
      <h3 className="gov-panel-title">
        <span className="gov-panel-icon">⚡</span>
        Skill Registry ({governanceState.skillRegistryCount} registered)
      </h3>
      <input
        className="gov-filter-input"
        placeholder="Filter skills..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <div className="gov-table-wrap">
        <table className="gov-table">
          <thead>
            <tr>
              <th>Skill</th>
              <th>Owner</th>
              <th>Version</th>
              <th>Trigger</th>
              <th>Tools</th>
              <th>Drift</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {skills.map((skill, i) => (
              <tr key={i} className={skill.driftFlag ? 'drift-row' : ''}>
                <td><code>{skill.name}</code></td>
                <td>{skill.owner}</td>
                <td>{skill.version}</td>
                <td>{skill.triggerValidated ? '✅' : '❌'}</td>
                <td>{skill.toolDepsVerified ? '✅' : '⚠️'}</td>
                <td>{skill.driftFlag ? <StatusBadge status="pending" /> : <StatusBadge status="COMPLIANT" />}</td>
                <td><StatusBadge status={skill.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Panel 5: Change Control ─────────────────────────────────────────────
const ChangeControlPanel = () => (
  <div className="gov-panel">
    <h3 className="gov-panel-title">
      <span className="gov-panel-icon">📝</span>
      Change Control Log
    </h3>
    <div className="gov-table-wrap">
      <table className="gov-table">
        <thead>
          <tr>
            <th>Change ID</th>
            <th>Layer</th>
            <th>Cascade</th>
            <th>Docs Updated</th>
            <th>Logged</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {governanceState.changeControl.map((chg, i) => (
            <tr key={i} className={chg.status === 'pending' ? 'pending-row' : ''}>
              <td><code>{chg.id}</code></td>
              <td>{chg.layer}</td>
              <td>{chg.cascadeCompleted ? '✅' : '🔄'}</td>
              <td>{chg.documentationUpdated ? '✅' : '❌'}</td>
              <td>{chg.logged ? '✅' : '❌'}</td>
              <td><StatusBadge status={chg.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="gov-change-rule">
      ⚖️ <em>No change closes without cascade confirmation — Article V, Heady Operating Constitution</em>
    </div>
  </div>
);

// ── Panel 6: Entropy Indicators ─────────────────────────────────────────
const EntropyIndicatorsPanel = () => {
  const e = governanceState.entropy;
  return (
    <div className="gov-panel">
      <h3 className="gov-panel-title">
        <span className="gov-panel-icon">📊</span>
        Entropy Indicators
      </h3>
      <div className="gov-entropy-grid">
        <div className="gov-entropy-item">
          <div className="gov-entropy-label">Skill Count Growth</div>
          <div className="gov-entropy-value">{e.skillCountGrowth}</div>
        </div>
        <div className="gov-entropy-item">
          <div className="gov-entropy-label">Instruction Length Growth</div>
          <div className="gov-entropy-value">{e.instructionLengthGrowth}</div>
        </div>
        <div className="gov-entropy-item">
          <div className="gov-entropy-label">Tool Rule Complexity</div>
          <div className="gov-entropy-value"><StatusBadge status={e.toolRuleComplexity} /></div>
        </div>
        <div className="gov-entropy-item">
          <div className="gov-entropy-label">Exception Clauses</div>
          <div className="gov-entropy-value">{e.exceptionClauses}</div>
        </div>
        <div className="gov-entropy-item">
          <div className="gov-entropy-label">Documentation Mismatches</div>
          <div className="gov-entropy-value">{e.documentationMismatches}</div>
        </div>
      </div>
      <div className="gov-entropy-trend">
        Overall Entropy Trend: <StatusBadge status={e.trend} />
        <span className="gov-entropy-note">
          {e.trend === 'low' ? '✨ System is structurally healthy' : '⚠️ Consolidation required before expansion'}
        </span>
      </div>
      <div className="gov-constitution-quote">
        <blockquote>
          "Governance scales faster than features." — <strong>Heady Operating Constitution, Article VIII</strong>
        </blockquote>
      </div>
    </div>
  );
};

// ── Skill Proposal Form ─────────────────────────────────────────────────
const SkillProposalForm = () => {
  const [form, setForm] = useState({
    name: '', owner: '', purpose: '', missionAlignment: '',
    overlapAnalysis: '', triggerConditions: '', toolDeps: '',
    outputSpec: '', sunsetCondition: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(`Skill "${form.name}" proposal submitted for governance review.`);
    setForm({ name: '', owner: '', purpose: '', missionAlignment: '', overlapAnalysis: '', triggerConditions: '', toolDeps: '', outputSpec: '', sunsetCondition: '' });
  };

  return (
    <div className="gov-panel gov-proposal-panel">
      <h3 className="gov-panel-title">
        <span className="gov-panel-icon">📋</span>
        Skill Proposal Form (SLMS Stage 1)
      </h3>
      <form className="gov-proposal-form" onSubmit={handleSubmit}>
        <div className="gov-form-row">
          <label>Skill Name</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="heady-new-skill" required />
        </div>
        <div className="gov-form-row">
          <label>Proposed Owner</label>
          <input value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} placeholder="CoreTeam" required />
        </div>
        <div className="gov-form-row">
          <label>Purpose</label>
          <textarea value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="What structural need does this skill solve?" rows={2} required />
        </div>
        <div className="gov-form-row">
          <label>Mission Alignment</label>
          <textarea value={form.missionAlignment} onChange={e => setForm({ ...form, missionAlignment: e.target.value })} placeholder="How does this support Heady's mission?" rows={2} required />
        </div>
        <div className="gov-form-row">
          <label>Overlap Analysis</label>
          <textarea value={form.overlapAnalysis} onChange={e => setForm({ ...form, overlapAnalysis: e.target.value })} placeholder="List existing skills checked for overlap" rows={2} required />
        </div>
        <div className="gov-form-row">
          <label>Trigger Conditions</label>
          <input value={form.triggerConditions} onChange={e => setForm({ ...form, triggerConditions: e.target.value })} placeholder="Precise invocation conditions" required />
        </div>
        <div className="gov-form-row">
          <label>Tool Dependencies</label>
          <input value={form.toolDeps} onChange={e => setForm({ ...form, toolDeps: e.target.value })} placeholder="Required tools, optional tools, forbidden tools" />
        </div>
        <div className="gov-form-row">
          <label>Output Specification</label>
          <input value={form.outputSpec} onChange={e => setForm({ ...form, outputSpec: e.target.value })} placeholder="Expected output format" />
        </div>
        <div className="gov-form-row">
          <label>Sunset Condition</label>
          <input value={form.sunsetCondition} onChange={e => setForm({ ...form, sunsetCondition: e.target.value })} placeholder="Under what condition is this deprecated?" required />
        </div>
        <button type="submit" className="gov-submit-btn">Submit for Governance Review</button>
      </form>
    </div>
  );
};

// ── Main Governance Dashboard ───────────────────────────────────────────
const GovernanceDashboard = () => {
  const [activePanel, setActivePanel] = useState('overview');

  const panels = [
    { id: 'overview', label: '🏗️ Overview', component: StructuralHealthPanel },
    { id: 'integrity', label: '📏 Integrity', component: InstructionIntegrityPanel },
    { id: 'compliance', label: '🔧 Compliance', component: ToolCompliancePanel },
    { id: 'registry', label: '⚡ Skills', component: SkillRegistryPanel },
    { id: 'changes', label: '📝 Changes', component: ChangeControlPanel },
    { id: 'entropy', label: '📊 Entropy', component: EntropyIndicatorsPanel },
    { id: 'proposal', label: '📋 Propose', component: SkillProposalForm },
  ];

  const ActiveComponent = panels.find(p => p.id === activePanel)?.component || StructuralHealthPanel;

  return (
    <div className="gov-dashboard">
      <div className="gov-header">
        <h2 className="gov-title">⚖️ Heady Governance Dashboard</h2>
        <span className="gov-subtitle">Structural Integrity Control Center • CTD v{governanceState.ctdVersion}</span>
      </div>

      <div className="gov-nav">
        {panels.map(panel => (
          <button
            key={panel.id}
            className={`gov-nav-btn ${activePanel === panel.id ? 'active' : ''}`}
            onClick={() => setActivePanel(panel.id)}
          >
            {panel.label}
          </button>
        ))}
      </div>

      <div className="gov-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activePanel}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <ActiveComponent />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GovernanceDashboard;
